import { index, pgTable, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// USER CONSENTS — ENROLL lane (docs/design/enroll-consent-neon.sql). The
// recorded-acceptance half of first-run consent: a user must explicitly
// accept the EULA + Data/Privacy Policy before continuing past sign-in, and
// that acceptance is a real, versioned, append-only row — never a boolean
// flag on `users` that a re-acceptance would silently overwrite.
//
// APPEND-ONLY BY DESIGN: accepting a NEW version of a doc inserts a NEW row.
// The full acceptance history for a user is therefore always reconstructable
// (which version, when, via which flow) — this mirrors driveExports'
// "never deleted, a new save is a new row" discipline
// (packages/schema/src/drive_export.ts) applied to a legal-acceptance record
// rather than a document export.
//
// user_id is `uuid` with a real FK — per CLAUDE.md's corrected 2026-06-22 law
// ("identity = per-user users.id uuid, text-keyed user_id tables are
// migration debt") and matching this package's newest precedent tables
// (entitlement_grants.ts, presence_profiles.ts, drive_export.ts) rather than
// perpetuating the older text-keyed convention into a brand-new table.
// =============================================================================

/**
 * One row per (user, doc, version) acceptance. Current-vs-accepted
 * resolution (missing / stale / current) is computed at read time by
 * comparing the most recent row per `doc` against the CURRENT version
 * constants (apps/api/lib/consentVersions.ts) — this table only ever
 * records what was actually accepted and when, never a derived state.
 */
export const userConsents = pgTable(
  'user_consents',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** 'eula' | 'privacy'. varchar, not a DB enum — house convention
     *  (jobs.source, accountKind, driveExports.artifactType all take a plain
     *  varchar over a Postgres enum so a future doc type is a data value, not
     *  a migration). */
    doc: varchar('doc', { length: 24 }).notNull(),
    /** e.g. '2026-07-05-draft' — a free-form version token, not a foreign
     *  key into anything: the CURRENT version for each doc lives as a
     *  constant in apps/api/lib/consentVersions.ts (ONE module, per the
     *  ENROLL brief), and this column simply records which token the user
     *  actually accepted at the time. */
    docVersion: varchar('doc_version', { length: 16 }).notNull(),
    acceptedAt: timestamp('accepted_at', { withTimezone: true }).notNull().defaultNow(),
    /** 'enrollment' | 'grandfather_prompt' — which flow produced this
     *  acceptance: a brand-new signup's first-run consent screen, or an
     *  existing (grandfathered) user re-prompted for a version they never
     *  saw. varchar, same house convention as `doc` above. */
    via: varchar('via', { length: 24 }).notNull(),
  },
  (t) => ({
    userIdx: index('user_consents_user_idx').on(t.userId),
    userDocIdx: index('user_consents_user_doc_idx').on(t.userId, t.doc),
  }),
);
